import {
	Stack,
	AsyncSelect,
	Alert,
	Box,
	Button,
	Icons,
	Table,
	Inline,
	Text,
	Dropdown,
	joinAttributes,
	RelativeTime,
	Time,
	Heading,
	Component,
	Select,
	Badge,
} from "@sembark-travel/ui-base"
import { Link, ButtonLink, useLocationQuery } from "@sembark-travel/ui-router"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { ListView, Search, useSearch } from "@sembark-travel/ui-list"
import { utcTimestampToLocalDate } from "@sembark-travel/datetime-utils"
import { useXHR } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import React, { Fragment, useEffect, useMemo } from "react"
import { Helmet } from "react-helmet-async"
import * as Validator from "yup"
import { generatePath } from "../router-utils"
import { IUser, XHR } from "./store"
import {
	Form,
	SubmissionError,
	SwitchInputField,
	validateFormValues,
	withServerErrors,
	TextInputField,
	RadioInputField,
	GetFieldValue,
	TextAreaInputField,
	SelectField,
	useForm,
	isTruthy,
	useFieldValue,
} from "@sembark-travel/ui-form"
import { IRole } from "../Roles"

export function List({ title = "Users" }: { title?: string }) {
	const [query, setQuery] = useLocationQuery()
	const [params, setParams] = useSearch(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Fragment>
			{title ? (
				<Helmet>
					<title>{title}</title>
				</Helmet>
			) : null}
			<Search
				title={title}
				initialParams={params}
				onSearch={(params) => {
					setParams({ ...params, page: 1 })
				}}
				Filters={Filters}
				actions={
					<ButtonLink to={generatePath("/users/new")} level="primary" anchored>
						Invite Member
					</ButtonLink>
				}
			>
				<ListView<IUser>
					pageKey="users-list"
					params={params}
					onPageChange={(page) => setParams({ ...params, page })}
					fetch={(xhr, params) =>
						XHR(xhr).getUsers({
							...params,
							include: "media,roles,last_activity_at,two_fa_enabled",
						})
					}
				>
					{({ items: users, refresh, xhr }) => (
						<Table
							striped
							bordered
							responsive
							headers={[
								"Name",
								"Roles",
								"User Since",
								"Recent Activity",
								"Status",
								"Billing Impact",
								"",
							]}
							rows={users.map((r) => [
								<Inline gap="2">
									{r.profile_thumb_image_url ? (
										<Box
											as="img"
											src={r.profile_thumb_image_url}
											size="12"
											alt="Avatar"
											rounded="lg"
											borderWidth="1"
											borderColor="default"
										/>
									) : (
										<Box
											size="12"
											width="12"
											height="12"
											display="flex"
											justifyContent="center"
											alignItems="center"
											bgColor="subtle"
											as="label"
											htmlFor="file"
											padding="4"
											backgroundColor="primary"
											borderColor="primary"
											borderRadius="md"
										>
											<Icons.User size="20" color="primary" />
										</Box>
									)}
									<Stack>
										<Box display={"flex"}>
											<Link
												to={generatePath("/users/:userId", {
													userId: r.id.toString(),
												})}
												color="accent"
												fontWeight="semibold"
												anchored
											>
												{r.name}
											</Link>
											{r.two_fa_enabled ? (
												<Box as="span" marginX={"2"} paddingBottom={"2"}>
													<Icons.TwoFAUser size="5" color="primary" />
												</Box>
											) : null}
										</Box>
										<Text fontSize="sm">
											{r.email}{" "}
											{r.disabled_at ? (
												<Icons.Ban color="warning" title="Access Disabled" />
											) : null}
										</Text>
									</Stack>
								</Inline>,
								<Box>
									{r.roles
										? joinAttributes(...r.roles.map((r) => r.name))
										: null}
								</Box>,
								<Box>
									{r.email_verified_at ? (
										<Time
											value={utcTimestampToLocalDate(r.email_verified_at)}
										/>
									) : (
										<Box>Email Not Verified</Box>
									)}
									<Box fontSize="sm" color="muted">
										Invited{" "}
										<RelativeTime
											value={utcTimestampToLocalDate(r.invited_at)}
										/>
									</Box>
								</Box>,
								<Box>
									{r.last_activity_at ? (
										<RelativeTime
											value={utcTimestampToLocalDate(r.last_activity_at)}
										/>
									) : (
										"-"
									)}
								</Box>,
								<Box>
									{r.deleted_at ? (
										<Badge danger>Deleted</Badge>
									) : r.disabled_at ? (
										<Badge warning>Disabled</Badge>
									) : !r.email_verified_at ? (
										<Badge warning>Pending</Badge>
									) : (
										<Badge success>Active</Badge>
									)}
								</Box>,
								<Box>
									{r.deleted_at ? (
										<Badge danger>
											<Inline alignItems="center" gap="1">
												<Icons.Trash />
												Not Included
											</Inline>
										</Badge>
									) : r.disabled_at ? (
										<Badge warning>
											<Inline alignItems="center" gap="1">
												<Icons.BankNotes />
												<Box>Included</Box>
											</Inline>
										</Badge>
									) : (
										<Badge success>
											<Inline alignItems="center" gap="1">
												<Icons.BankNotes />
												<Box>Included</Box>
											</Inline>
										</Badge>
									)}
								</Box>,
								!r.deleted_at ? (
									r.is_owner && r.email_verified_at ? null : (
										<Box textAlign="right">
											<Dropdown alignRight>
												<Button level="tertiary" as={Dropdown.Toggle} size="sm">
													<Icons.DotsVertical />
												</Button>
												<Dropdown.Menu>
													<ResendInvitation user={r} onChange={() => refresh()}>
														{({ onResend, label }) => (
															<Dropdown.MenuItem onClick={onResend}>
																<Inline gap={"1"} alignItems={"center"}>
																	<Icons.Mail />
																	{label}
																</Inline>
															</Dropdown.MenuItem>
														)}
													</ResendInvitation>
													<DisableDeleteAccount
														user={r}
														onSuccess={() => refresh()}
													>
														{({ onWipe }) => (
															<Dropdown.MenuItem
																onClick={onWipe}
																color="warning"
															>
																{!r.email_verified_at || r.disabled_at ? (
																	<Inline gap="1" alignItems={"center"}>
																		<Icons.Trash /> Delete Account
																	</Inline>
																) : (
																	<Inline gap="1" alignItems={"center"}>
																		<Icons.Ban />
																		Delete / Disable Account
																	</Inline>
																)}
															</Dropdown.MenuItem>
														)}
													</DisableDeleteAccount>
													<EnableAccount user={r} onSuccess={() => refresh()}>
														{({ onEnable }) => (
															<Dropdown.MenuItem
																onClick={onEnable}
																color="success"
															>
																<Icons.Refresh opacity="50" /> Enable Account
															</Dropdown.MenuItem>
														)}
													</EnableAccount>
													{!r.email_verified_at || r.disabled_at ? null : (
														<Component initialState={false}>
															{({ state, setState }) => (
																<Dropdown.MenuItem
																	onClick={async () => {
																		if (state) return
																		if (
																			!window.confirm(
																				`Are you sure you want to ${
																					r.password_login_disabled_at
																						? "Enabled"
																						: "Disable"
																				} login via Password`
																			)
																		) {
																			return
																		}
																		setState(true)
																		try {
																			await xhr.patch(
																				`/users/${r.id}/toggle-password-login`
																			)
																			refresh()
																		} catch (e) {
																			const error = e as Error
																			alert(
																				error.message ||
																					"Something went wrong. Please try after sometime."
																			)
																		}
																		setState(false)
																	}}
																>
																	<Icons.FingerPrint />{" "}
																	{r.password_login_disabled_at
																		? "Enable"
																		: "Disable"}{" "}
																	Password Login
																</Dropdown.MenuItem>
															)}
														</Component>
													)}
												</Dropdown.Menu>
											</Dropdown>
										</Box>
									)
								) : (
									<Box>
										<Box color="warning">
											<Icons.Trash /> by {r.deleted_by?.name}
										</Box>
										<Box fontSize="sm">
											<RelativeTime
												value={utcTimestampToLocalDate(r.deleted_at)}
											/>
										</Box>
									</Box>
								),
							])}
						/>
					)}
				</ListView>
			</Search>
		</Fragment>
	)
}

function Filters() {
	const form = useForm()

	const { value: statusOnlyDisabled } = useFieldValue("only_disabled")
	const { value: statusOnlyDeleted } = useFieldValue("only_deleted")

	return (
		<>
			<RadioInputField
				label="Only Disabled"
				name="only_disabled"
				value={"1"}
				onClick={(e) => {
					if (isTruthy(statusOnlyDisabled)) {
						form.change(e.currentTarget.name, undefined)
					}
				}}
				onChange={(e) => {
					form.change(e.currentTarget.name, e.currentTarget.value)
					form.change("only_deleted", undefined)
				}}
			/>
			<RadioInputField
				label="Only Deleted"
				value={"1"}
				name="only_deleted"
				onClick={(e) => {
					if (isTruthy(statusOnlyDeleted)) {
						form.change(e.currentTarget.name, undefined)
					}
				}}
				onChange={(e) => {
					form.change(e.currentTarget.name, e.currentTarget.value)
					form.change("only_disabled", undefined)
				}}
			/>
		</>
	)
}

/**
 * Resend the signup invitation, or the email verification once the user has
 * signed up (the backend decides; an invitation would reset their password).
 */
export function ResendInvitation({
	user,
	onChange,
	children,
}: {
	user: IUser
	onChange: () => void
	children?: (props: { onResend: () => void; label: string }) => React.ReactNode
}) {
	const label = user.signup_at
		? "Resend Verification Email"
		: "Resend Invitation"
	const xhr = useXHR()
	const origin = window.location.origin || ""
	const initialValues = useMemo(() => {
		return {
			invited_signup_link: origin ? `${origin}/invited-signup` : "",
			email: user.email,
			name: user.name,
		}
	}, [origin, user])
	const [isOpen, open, close] = useDialog()
	if (user.email_verified_at || user.deleted_at) return null
	return (
		<>
			{children ? (
				children({ onResend: open, label })
			) : (
				<Button level="secondary" onClick={open}>
					{label}
				</Button>
			)}
			<Dialog open={isOpen} onClose={close} title={label} sm>
				<Form<typeof initialValues>
					initialValues={initialValues}
					onSubmit={withServerErrors(async (values) => {
						const { data } = await xhr.patch(
							`/invited-users/${user.uid}/resend`,
							values
						)
						close()
						onChange()
						showSnackbar(data.message || `Invitation resent to ${values.name}`)
					})}
					validate={validateFormValues(
						Validator.object().shape({
							name: Validator.string()
								.required("Please provide a name.")
								.max(191, "Please use 191 or fewer characters."),
							email: Validator.string()
								.email("Please provide a valid email address")
								.required("Please provide an email address."),
						})
					)}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form onSubmit={handleSubmit} noValidate>
							<Dialog.Body>
								<Stack gap="4">
									<Text color="muted">
										<Icons.Info /> You can update the name/email if necessary.
									</Text>
									<TextInputField
										name="name"
										label="Name"
										type="text"
										required
									/>
									<TextInputField
										name="email"
										label="Email"
										type="email"
										required
									/>
									<input
										hidden
										type="hidden"
										name="invited_signup_link"
										value={initialValues.invited_signup_link}
									/>
									<SubmissionError />
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting} autoFocus>
									<Icons.Mail /> {submitting ? "Resending... " : label}
								</Button>
								<Button onClick={close} disabled={submitting}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

type TReason = {
	name: string
	id: number
}

const ReasonForDelete: Array<TReason> = [
	{ name: "Employee left the organisation", id: 1 },
	{ name: "Software access not needed", id: 2 },
	{ name: "Others", id: 3 },
]

const ReasonForDisable: Array<TReason> = [
	{ name: "Training Period", id: 4 },
	{ name: "On temporary leave (will join back)", id: 5 },
	{ name: "Employee left", id: 6 },
	{ name: "Others", id: 7 },
]

const ReasonForInactiveDelete: Array<TReason> = [
	{ name: "User doesn't require", id: 8 },
	{ name: "Invitation sent by mistake", id: 9 },
	{ name: "Others", id: 10 },
]

const disableFormSchema = Validator.object().shape({
	_method: Validator.string().required(
		"Please provide a name for the account e.g. Office Expense"
	),
	category: Validator.object().required("Please select a reason"),
	reason: Validator.string().when("category", {
		is: (val: TReason | undefined) => val && val.name === "Others",
		then: (schema) => schema.required("Please add comment"),
		otherwise: (schema) => schema.notRequired(),
	}),
	confirmationBox: Validator.number().when("_method", {
		is: (val: string) => val === "disable",
		then: (schema) =>
			schema.test(
				"is-valid-confirmation",
				"This confirmation is required to proceed",
				(value) => value !== undefined && value !== 0
			),
		// .typeError("This confirmation must be a number"),
		otherwise: (schema) => schema.notRequired(),
	}),
})

export function DisableDeleteAccount({
	user,
	onSuccess,
	children,
}: {
	user: IUser
	onSuccess: () => void
	children: (props: { onWipe: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()
	// if (user.disabled_at) return null
	return (
		<>
			{children({ onWipe: open })}
			<Dialog
				open={isOpen}
				onClose={close}
				title={
					!user.email_verified_at || user.disabled_at
						? "Delete Account"
						: "Delete / Disable Account"
				}
				sm
			>
				<Form<{
					category: { name: string }
					_method: string
					confirmationBox?: string
					reason: string
				}>
					validate={validateFormValues(disableFormSchema)}
					onSubmit={withServerErrors(async (values) => {
						let url, payload
						const { _method, category, reason } = values
						if (_method === "disable") {
							url = `/disabled-users/${user.uid}`
							payload = {
								category: category?.name,
								reason,
							}
						} else {
							url = `/users/${user.uid}`
							payload = {
								_method: "delete",
								category: category?.name,
								reason,
							}
						}
						const { data } = await xhr.post(url, payload as object)
						close()
						onSuccess()
						showSnackbar(
							data.message || `${user.name}'s account access has been disabled.`
						)
					})}
					subscription={{ submitting: true }}
					initialValues={
						!user.email_verified_at || user.disabled_at
							? { _method: "delete" }
							: {}
					}
				>
					{({ submitting, handleSubmit, form }) => (
						<form noValidate onSubmit={handleSubmit}>
							{/* <FormSpy subscription={{ valid: true, error: true }}></FormSpy> */}
							<Dialog.Body>
								<Stack gap="2">
									<Stack gap={"2"}>
										<Heading as="h5" fontSize="md">
											Please review account details:
										</Heading>
									</Stack>
									<Inline
										borderWidth="1"
										rounded="lg"
										flexWrap="wrap"
										bgColor="primary"
										gap="8"
										padding="4"
									>
										<Stack>
											<Box
												textTransform="uppercase"
												fontSize="sm"
												fontWeight="semibold"
												color="muted"
											>
												NAME
											</Box>
											<Box fontWeight="semibold" fontSize="base">
												{user.name}
											</Box>
											<Box fontWeight="normal" fontSize="sm">
												{user.email}
											</Box>
										</Stack>

										<Stack>
											<Box
												textTransform="uppercase"
												fontSize="sm"
												fontWeight="semibold"
												color="muted"
											>
												{pluralize("Role", user.roles?.length || 1)}
											</Box>
											<Box fontWeight="semibold">
												{user.roles?.map((r) => r.name).join(", ")}
											</Box>
										</Stack>
									</Inline>
									{!user.email_verified_at || user.disabled_at ? null : (
										<Heading as="h5" fontSize="md">
											Select Anyone
										</Heading>
									)}

									<GetFieldValue<"delete" | "disable"> name="_method">
										{({ value: _method }) => (
											<Inline justifyContent="between" flexWrap="wrap">
												<Box
													paddingX="2"
													paddingY="1"
													borderWidth="1"
													borderColor="transparent"
													{...(_method === "delete" && {
														borderRadius: "md",
														backgroundColor: "danger",
														color: "danger",
														borderWidth: "1",
														borderColor: "danger",
													})}
													onClick={() => {
														form.change("category" as never, undefined as never)
													}}
													flex="1"
												>
													<Stack
														borderRadius="md"
														paddingBottom="2"
														borderColor="muted"
														borderWidth="1"
														as="label"
														overflow="hidden"
														backgroundColor={{ hover: "danger" }}
													>
														<Box display={"none"}>
															<RadioInputField
																disabled={
																	!user.email_verified_at || !!user.disabled_at
																}
																name="_method"
																value="delete"
															/>
														</Box>

														<Inline
															fontSize="base"
															bgColor="danger_emphasis"
															color="on_emphasis"
															padding="2"
															fontWeight="semibold"
															textAlign="center"
															gap="1"
															alignItems={"center"}
														>
															<Icons.Trash size="6" />
															If You Delete
														</Inline>
														<Stack
															padding="2"
															gap="2"
															fontSize="sm"
															color="success"
															letterSpacing="wider"
														>
															<Inline gap="1">
																<Icons.Ban />
																No More Charges
															</Inline>
															<Inline gap="1">
																<Icons.DocumentDownload />
																Your Data will be saved
															</Inline>
															<Inline gap="1">
																<Icons.User />
																User isn’t able to login
															</Inline>
														</Stack>
													</Stack>
												</Box>
												{!user.email_verified_at || user.disabled_at ? null : (
													<Box
														paddingX="2"
														paddingY="1"
														borderWidth="1"
														borderColor="transparent"
														{...(_method === "disable" && {
															borderRadius: "md",
															backgroundColor: "warning",
															color: "danger",
															borderColor: "warning",
														})}
														flex="1"
														onClick={() => {
															form.change(
																"category" as never,
																undefined as never
															)
														}}
													>
														<Stack
															borderRadius="md"
															paddingBottom="2"
															borderWidth="1"
															borderColor="muted"
															as="label"
															overflow="hidden"
															backgroundColor={{ hover: "warning" }}
														>
															<Box display={"none"}>
																<RadioInputField
																	name="_method"
																	value="disable"
																/>
															</Box>
															<Inline
																fontSize="base"
																color="on_emphasis"
																padding="2"
																fontWeight="semibold"
																gap="1"
																style={{
																	backgroundColor: "rgba(173, 87, 0, 1)",
																}}
															>
																<Icons.Ban size="6" />
																If You Disable
															</Inline>
															<Stack
																padding="2"
																gap="2"
																fontSize="sm"
																color="danger"
															>
																<Inline gap="1">
																	<Icons.BankNotes />
																	Charges Still Apply
																</Inline>
																<Inline gap="1">
																	<Icons.Reactive />
																	User can be re-enabled at anytime
																</Inline>
																<Inline gap="1">
																	<Icons.CalendarCross color="danger" />
																	User can't receive leads
																</Inline>
															</Stack>
														</Stack>
													</Box>
												)}
											</Inline>
										)}
									</GetFieldValue>

									<GetFieldValue<"delete" | "disable"> name="_method">
										{({ value: _method }) =>
											_method ? (
												<Stack gap="2">
													<SelectField
														select={Select}
														name="category"
														required
														label="Reason"
														placeholder="Select a reason"
														options={
															_method === "disable"
																? ReasonForDisable
																: !user.email_verified_at
																	? ReasonForInactiveDelete
																	: ReasonForDelete
														}
														fullWidth
													/>

													<TextAreaInputField
														label="Comment"
														name={"reason"}
														placeholder="Body of the comment here...."
													/>

													{_method === "disable" ? (
														<Stack gap="2">
															<SwitchInputField
																required
																fontSize="sm"
																fontWeight="normal"
																name="confirmationBox"
																label="I understand that charges will still apply if this user is disabled"
																value="1"
															/>
														</Stack>
													) : null}
												</Stack>
											) : null
										}
									</GetFieldValue>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="2" justifyContent="end">
										<GetFieldValue<"delete" | "disable"> name="_method">
											{({ value: _method }) =>
												_method ? (
													<>
														<Button onClick={close}>Cancel</Button>
														<Button
															type="submit"
															level="primary"
															status="danger"
															disabled={submitting}
														>
															{submitting
																? "Submiting... "
																: _method === "disable"
																	? "Disable User"
																	: "Delete User"}
														</Button>
													</>
												) : null
											}
										</GetFieldValue>
									</Inline>
								</Stack>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

export function EnableAccount({
	user,
	onSuccess,
	children,
}: {
	user: IUser
	onSuccess: () => void
	children?: (props: { onEnable: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isOpen, open, close] = useDialog()
	if (!user.disabled_at) return null
	return (
		<>
			{children ? (
				children({ onEnable: open })
			) : (
				<Button level="secondary" onClick={open}>
					<Icons.Refresh /> Enable Account
				</Button>
			)}
			<Dialog open={isOpen} onClose={close} title="Enable Account" sm>
				<Form<object>
					onSubmit={withServerErrors(async () => {
						const { data } = await xhr.delete(`/disabled-users/${user.uid}`)
						close()
						onSuccess()
						showSnackbar(
							data.message || `${user.name}'s account has been enabled.`
						)
					})}
					subscription={{ submitting: true }}
				>
					{({ submitting, handleSubmit }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="6">
									<Stack gap="2">
										<Heading as="h5" fontSize="md">
											Please review account details:
										</Heading>
									</Stack>
									<Inline
										borderWidth="1"
										rounded="lg"
										flexWrap="wrap"
										bgColor="primary"
										gap="4"
										padding="4"
									>
										<Stack gap="1">
											<Box
												textTransform="uppercase"
												fontSize="sm"
												fontWeight="semibold"
												color="muted"
											>
												Name
											</Box>
											<Box fontWeight="semibold">{user.name}</Box>
											<Box fontSize="sm">{user.email}</Box>
										</Stack>
										{user.roles ? (
											<Stack gap="1">
												<Box
													textTransform="uppercase"
													fontSize="sm"
													fontWeight="semibold"
													color="muted"
												>
													{pluralize("Role", user.roles.length)}
												</Box>
												<Box fontWeight="semibold">
													{user.roles.map((r) => r.name).join(", ")}
												</Box>
											</Stack>
										) : null}
									</Inline>
									<Alert status="warning">
										<Stack gap="2">
											<Text>
												Following things will happen when the account is
												enabled.
											</Text>
											<Stack
												as="ul"
												marginLeft="4"
												listStyleType="disc"
												gap="1"
											>
												<li>
													{user.name} &lt;{user.email}&gt; will be able to login
													using this account.
												</li>
											</Stack>
										</Stack>
									</Alert>
									<SubmissionError />
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button
									type="submit"
									level="primary"
									status="warning"
									disabled={submitting}
								>
									<Icons.Refresh />{" "}
									{submitting
										? "Enabling... "
										: "I understand, Enable this account."}
								</Button>
								<Button onClick={close}>Cancel</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

type SelectUsersProps = Omit<
	React.ComponentProps<typeof AsyncSelect>,
	"fetch"
> & {
	roles?: Array<IRole>
	permissions?: Array<string>
	searchOnlyByName?: 0 | 1
	excludeSelf?: 0 | 1
	withDeleted?: 0 | 1
	sortBy?: "active_first"
}

export function SelectUsers({
	roles,
	permissions,
	searchOnlyByName,
	excludeSelf,
	withDeleted,
	sortBy = "active_first",
	...props
}: SelectUsersProps) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			multiple
			{...props}
			perFetchLimit={15}
			fetch={(q, { page }) =>
				XHR(xhr)
					.getUsers({
						search_only_by_name: searchOnlyByName,
						exclude_self: excludeSelf,
						with_deleted: withDeleted,
						sort_by: sortBy,
						limit: 15,
						page,
						q,
						roles: roles?.length ? roles.map((r) => r.id) : null,
						permissions: permissions,
					})
					.then((resp) => resp.data)
			}
			optionRenderer={({ option }: { option: IUser }) => {
				const { name, deleted_at, disabled_at } = option
				let statusBadge: JSX.Element | null = null
				if (deleted_at) {
					statusBadge = (
						<Icons.Trash color="danger" title={`${name} account deleted`} />
					)
				} else if (disabled_at) {
					statusBadge = (
						<Icons.Ban color="warning" title={`${name} account disabled`} />
					)
				}
				return (
					<Inline gap="2" justifyContent="between" alignItems="center">
						<Text>{name}</Text>
						<Inline>{statusBadge}</Inline>
					</Inline>
				)
			}}
		/>
	)
}
